export type LegacyPermissionInput = {
  permission: string;
  enabled: boolean;
};

export type UserSecurityPolicyInput = {
  policy: string;
  allow: boolean;
};

export type LegacyAwareAssignments = {
  policyPayload: UserSecurityPolicyInput[];
  legacyPermissions: LegacyPermissionInput[];
};

const LEGACY_PREFIX = 'legacy:';

// These legacy permission fields do not follow the *_access / *editparticipant naming.
const LEGACY_NON_STANDARD_ACCESS_FIELDS = new Set([
  'caseconferencingtool',
  'dashboard_editor',
  'charges_editor',
  'changelogs_editor'
]);

const parsePolicyList = (value: string[]): string[] => {
  const parts = (value || [])
    .map((item) => `${item || ''}`.trim())
    .filter((item) => item.length > 0);
  return Array.from(new Set(parts));
};

const normalizeToken = (value: string) => `${value || ''}`.trim().toLowerCase().replace(/\s+/g, ' ');

const getLegacyValue = (policy: string): string => {
  const trimmed = `${policy || ''}`.trim();
  if (!trimmed) return '';
  if (!trimmed.toLowerCase().startsWith(LEGACY_PREFIX)) return '';
  const raw = trimmed.slice(LEGACY_PREFIX.length).trim();
  if (raw.startsWith('[') && raw.endsWith(']')) {
    return raw.slice(1, -1).trim();
  }
  return raw;
};

const isLegacyAccessField = (legacyValue: string): boolean => {
  const normalized = normalizeToken(legacyValue);
  if (!normalized) return false;
  if (normalized.endsWith('access')) return true;
  if (normalized.endsWith('editparticipant')) return true;
  return LEGACY_NON_STANDARD_ACCESS_FIELDS.has(normalized);
};

const normalizeLegacyAccessField = (legacyValue: string): string => {
  return normalizeToken(legacyValue).replace(/\s+/g, '');
};

export const buildLegacyAwareAssignments = (selectedPolicies: string[]): LegacyAwareAssignments => {
  const unique = parsePolicyList(selectedPolicies);
  const policyPayload: UserSecurityPolicyInput[] = [];
  const legacyPermissionSet = new Set<string>();

  unique.forEach((policy) => {
    const legacyValue = getLegacyValue(policy);
    if (!legacyValue) {
      policyPayload.push({ policy, allow: true });
      return;
    }

    if (isLegacyAccessField(legacyValue)) {
      const permission = normalizeLegacyAccessField(legacyValue);
      if (!permission) return;
      legacyPermissionSet.add(permission);
      return;
    }
  });

  const legacyPermissions = Array.from(legacyPermissionSet).map((permission) => ({
    permission,
    enabled: true
  }));

  return {
    policyPayload,
    legacyPermissions
  };
};
